"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Upload, Eye, Download, Calendar, DollarSign, TrendingUp, BarChart3, Info, AlertCircle, Loader2, RefreshCw, Edit3, Trash2, Save, X, Plus, Search, Building2, Target } from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@mantis-sas/ui";
import BulkUploadModal from "../../../components/BulkUploadModal";
import {
  getBudgets,
  createBudget,
  updateBudget,
  deleteBudget,
  updateSpentAmount,
  getBudgetSummary,
  getBudgetVarianceAnalysis,
  formatBulkBudgetData,
  validateBudgetData,
  bulkCreateBudgets,
} from "../../../../ApiCalls/mantisFlowsBudgets";
import { getCostCenters } from "../../../../ApiCalls/mantisFlowsCategories";

export default function BudgetConfigurationModal({
  clientId,
  onClose,
  embedded = false,
}) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBudget, setNewBudget] = useState({
    name: "",
    amount: "",
    currency: "COP",
    start_date: "",
    end_date: "",
    cost_center_id: "",
    description: "",
  });
  const [costCenters, setCostCenters] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [budgetData, setBudgetData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [, setVarianceData] = useState(null);
  const [, setUploadedData] = useState([]);
  const [isLoadingBudgets, setIsLoadingBudgets] = useState(true);
  const [uploadResult, setUploadResult] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [isDeleting, setIsDeleting] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    cost_center_id: "",
    entity_table: "",
    entity_id: "",
    start_date: "",
    end_date: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  const { toast } = useToast();

  // Configuración para el BulkUploadModal
  const uploadConfig = {
    requiredFields: ["name", "amount", "currency"],
    fieldMappings: {
      name: { label: "Nombre del Presupuesto" },
      amount: { label: "Monto" },
      currency: { label: "Moneda" },
      start_date: { label: "Fecha de Inicio" },
      end_date: { label: "Fecha de Fin" },
      status: { label: "Estado" },
      spent_amount: { label: "Monto Gastado" },
      cost_center_id: { label: "ID Centro de Costo" },
      description: { label: "Descripción" },
      entity_table: { label: "Tabla de Entidad" },
      entity_id: { label: "ID de Entidad" },
    },
    validateItem: (item) => {
      return validateBudgetData(item);
    },
  };

  // Cargar datos de presupuesto al montar el componente
  useEffect(() => {
    loadBudgetData();
    loadCostCenters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, filters]);

  // Función para cargar centros de costo
  const loadCostCenters = async () => {
    try {
      const response = await getCostCenters(clientId);
      if (response.success) {
        setCostCenters(response.data || []);
      } else {
        console.error("Error loading cost centers:", response.error);
        setCostCenters([]);
      }
    } catch (error) {
      console.error("Error loading cost centers:", error);
      setCostCenters([]);
    }
  };

  // Función para cargar datos desde la API
  const loadBudgetData = async () => {
    setIsLoadingBudgets(true);
    try {
      console.log(
        "Loading budget data for clientId:",
        clientId,
        "filters:",
        filters
      );

      // Preparar filtros para la API (excluir "all" para status)
      const apiFilters = { ...filters };
      if (apiFilters.status === "all") {
        delete apiFilters.status;
      }

      // Cargar presupuestos
      const budgetsResponse = await getBudgets(clientId, apiFilters);

      if (budgetsResponse.success) {
        console.log("Budgets response:", budgetsResponse.data);
        setBudgetData(budgetsResponse.data || []);

        // Cargar resumen
        const summaryResponse = await getBudgetSummary(clientId, apiFilters);
        if (summaryResponse.success) {
          setSummaryData(summaryResponse.data);
        }

        // Cargar análisis de variación
        const varianceResponse = await getBudgetVarianceAnalysis(
          clientId,
          apiFilters
        );
        if (varianceResponse.success) {
          setVarianceData(varianceResponse.data);
        }
      } else {
        console.log("API response not successful:", budgetsResponse.error);
        setBudgetData([]);
        setSummaryData(null);
        setVarianceData(null);
      }
    } catch (error) {
      console.error("Error loading budget data:", error);
      toast({
        title: "Error al cargar datos",
        description: "No se pudieron cargar los datos de presupuesto.",
        variant: "destructive",
      });
      setBudgetData([]);
      setSummaryData(null);
      setVarianceData(null);
    } finally {
      setIsLoadingBudgets(false);
    }
  };

  // Formatear números como moneda
  const formatCurrency = (amount, currency = "COP") => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("es-CO");
  };

  // Obtener color del badge según el estado
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700 border-green-200";
      case "inactive":
        return "bg-gray-50 text-gray-700 border-gray-200";
      case "completed":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "cancelled":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  // Filtrar datos según término de búsqueda
  const filteredData = budgetData.filter((budget) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      budget.name?.toLowerCase().includes(searchLower) ||
      budget.description?.toLowerCase().includes(searchLower) ||
      budget.cost_center_name?.toLowerCase().includes(searchLower) ||
      budget.entity_table?.toLowerCase().includes(searchLower)
    );
  });

  const handleUploadSuccess = async (data, options = {}) => {
    setLoading(true);
    try {
      // Formatear datos del Excel
      const formattedResult = formatBulkBudgetData(data, clientId);

      if (!formattedResult.success) {
        setUploadResult({
          success: false,
          errors: [formattedResult.error],
          successfulItems: 0,
          failedItems: data.length,
        });
        setLoading(false);
        return;
      }

      // Enviar datos a la API
      const response = await bulkCreateBudgets(clientId, formattedResult.data);

      if (response.success) {
        setUploadResult({
          success: true,
          successfulItems: formattedResult.data.length,
          failedItems: 0,
          errors: [],
        });

        setUploadedData(data);

        // Recargar datos desde la API
        await loadBudgetData();
      } else {
        setUploadResult({
          success: false,
          errors: [
            response.error ||
              "No se pudieron guardar los datos de presupuesto.",
          ],
          successfulItems: 0,
          failedItems: formattedResult.data.length,
        });
      }
    } catch (error) {
      console.error("Error processing upload:", error);
      setUploadResult({
        success: false,
        errors: ["Hubo un error al procesar el archivo. Verifica el formato."],
        successfulItems: 0,
        failedItems: data.length,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Crear datos de ejemplo para la plantilla
    const templateData = [
      {
        name: "Presupuesto Ejemplo",
        amount: 100000000,
        currency: "COP",
        start_date: "2025-01-01",
        end_date: "2025-12-31",
        status: "active",
        spent_amount: 0,
        cost_center_id: "",
        description: "Presupuesto de ejemplo para el departamento",
        entity_table: "",
        entity_id: "",
      },
    ];

    // Convertir a CSV
    const headers = [
      "name",
      "amount",
      "currency",
      "start_date",
      "end_date",
      "status",
      "spent_amount",
      "cost_center_id",
      "description",
      "entity_table",
      "entity_id",
    ];
    const csvContent = [
      headers.join(","),
      ...templateData.map((row) =>
        headers.map((header) => row[header] || "").join(",")
      ),
    ].join("\n");

    // Descargar archivo
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `plantilla_presupuestos.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Plantilla descargada",
      description:
        "La plantilla de presupuestos se ha descargado correctamente.",
      variant: "default",
    });
  };

  // Función para iniciar edición
  const handleStartEdit = (index, budget) => {
    setEditingRow(index);
    setEditedData({
      id: budget.id,
      name: budget.name,
      amount: budget.amount,
      currency: budget.currency,
      start_date: budget.start_date,
      end_date: budget.end_date,
      status: budget.status,
      spent_amount: budget.spent_amount,
      cost_center_id: budget.cost_center_id,
      description: budget.description,
      entity_table: budget.entity_table,
      entity_id: budget.entity_id,
    });
  };

  // Función para cancelar edición
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedData({});
  };

  // Función para guardar cambios
  const handleSaveEdit = async () => {
    if (!editedData.id) {
      toast({
        title: "Error",
        description: "No se puede editar este registro. ID no encontrado.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await updateBudget(clientId, editedData.id, editedData);

      if (response.success) {
        toast({
          title: "Presupuesto actualizado",
          description: "Los cambios se han guardado correctamente.",
          variant: "default",
        });

        // Recargar datos
        await loadBudgetData();
        handleCancelEdit();
      } else {
        toast({
          title: "Error al actualizar",
          description: response.error || "No se pudieron guardar los cambios.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating budget:", error);
      toast({
        title: "Error",
        description: "Hubo un error al actualizar el presupuesto.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para abrir modal de confirmación de eliminación
  const handleDeleteClick = (budgetId, budgetName) => {
    if (!budgetId) {
      toast({
        title: "Error",
        description: "No se puede eliminar este registro. ID no encontrado.",
        variant: "destructive",
      });
      return;
    }

    setBudgetToDelete({
      id: budgetId,
      name: budgetName,
    });
    setDeleteModalOpen(true);
  };

  // Función para confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!budgetToDelete) return;

    setIsDeleting(budgetToDelete.id);
    try {
      const response = await deleteBudget(clientId, budgetToDelete.id);

      if (response.success) {
        toast({
          title: "Presupuesto eliminado",
          description: `El presupuesto "${budgetToDelete.name}" ha sido eliminado.`,
          variant: "default",
        });

        // Recargar datos
        await loadBudgetData();
        setDeleteModalOpen(false);
        setBudgetToDelete(null);
      } else {
        toast({
          title: "Error al eliminar",
          description: response.error || "No se pudo eliminar el presupuesto.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting budget:", error);
      toast({
        title: "Error",
        description: "Hubo un error al eliminar el presupuesto.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  // Función para cancelar eliminación
  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setBudgetToDelete(null);
  };

  // Función para actualizar monto gastado
  
  // Función para crear nuevo presupuesto
  const handleCreateBudget = async () => {
    // Validar datos
    const validation = validateBudgetData(newBudget);
    if (!validation.isValid) {
      toast({
        title: "Error de validación",
        description: validation.errors.join(", "),
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const budgetData = {
        ...newBudget,
        amount: parseFloat(newBudget.amount),
        spent_amount: 0, // Set default spent amount to 0
        status: "active", // Set default status
        cost_center_id:
          newBudget.cost_center_id === "none" ? null : newBudget.cost_center_id,
      };

      const response = await createBudget(clientId, budgetData);

      if (response.success) {
        toast({
          title: "Presupuesto creado",
          description: "El presupuesto se ha creado correctamente.",
          variant: "default",
        });

        // Recargar datos
        await loadBudgetData();

        // Limpiar formulario y cerrar modal
        setNewBudget({
          name: "",
          amount: "",
          currency: "COP",
          start_date: "",
          end_date: "",
          cost_center_id: "",
          description: "",
        });
        setShowCreateModal(false);
      } else {
        toast({
          title: "Error al crear",
          description: response.error || "No se pudo crear el presupuesto.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error creating budget:", error);
      toast({
        title: "Error",
        description: "Hubo un error al crear el presupuesto.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Función para cancelar creación
  const handleCancelCreate = () => {
    setNewBudget({
      name: "",
      amount: "",
      currency: "COP",
      start_date: "",
      end_date: "",
      cost_center_id: "",
      description: "",
    });
    setShowCreateModal(false);
  };

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="p-8 space-y-8">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <DollarSign className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-slate-600 text-lg leading-relaxed text-left">
                  {isLoadingBudgets
                    ? "Cargando datos de presupuesto..."
                    : budgetData.length > 0
                    ? `Gestiona los presupuestos de tu organización (${budgetData.length} presupuestos cargados)`
                    : "Carga y gestiona los presupuestos de tu organización"}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={loadBudgetData}
                disabled={isLoadingBudgets}
                className="flex items-center space-x-2"
              >
                {isLoadingBudgets ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span>Actualizar</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Descargar Plantilla</span>
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Nuevo Presupuesto</span>
              </Button>
              <Button
                onClick={() => setShowUploadModal(true)}
                disabled={loading}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white flex items-center space-x-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                <span>{loading ? "Procesando..." : "Cargar Presupuestos"}</span>
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          {summaryData && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded p-6 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">
                      Total Presupuestado
                    </p>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(summaryData.totalAmount)}
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-blue-600" />
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded p-6 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-600 text-sm font-medium">
                      Total Gastado
                    </p>
                    <p className="text-2xl font-bold text-green-900">
                      {formatCurrency(summaryData.totalSpent)}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded p-6 border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-600 text-sm font-medium">
                      Presupuestos Activos
                    </p>
                    <p className="text-2xl font-bold text-orange-900">
                      {summaryData.statusSummary?.active?.count || 0}
                    </p>
                  </div>
                  <Target className="h-8 w-8 text-orange-600" />
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded p-6 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-600 text-sm font-medium">
                      Total Presupuestos
                    </p>
                    <p className="text-2xl font-bold text-purple-900">
                      {summaryData.totalBudgets}
                    </p>
                  </div>
                  <Building2 className="h-8 w-8 text-purple-600" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Filtros</h3>
            <Button
              variant="outline"
              onClick={() =>
                setFilters({
                  status: "all",
                  cost_center_id: "",
                  entity_table: "",
                  entity_id: "",
                  start_date: "",
                  end_date: "",
                })
              }
              className="text-sm"
            >
              Limpiar Filtros
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="status-filter">Estado</Label>
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  setFilters({ ...filters, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                  <SelectItem value="completed">Completado</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="search">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="search"
                  placeholder="Buscar por nombre, descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="start-date">Fecha Inicio</Label>
              <Input
                id="start-date"
                type="date"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters({ ...filters, start_date: e.target.value })
                }
              />
            </div>

            <div>
              <Label htmlFor="end-date">Fecha Fin</Label>
              <Input
                id="end-date"
                type="date"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters({ ...filters, end_date: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        {/* Budgets Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Eye className="h-6 w-6 text-slate-600" />
                <div>
                  <p className="text-slate-600">
                    {isLoadingBudgets
                      ? "Obteniendo información..."
                      : budgetData.length > 0
                      ? `${budgetData.length} presupuestos disponibles`
                      : "No hay presupuestos disponibles. Crea uno nuevo o carga desde archivo."}
                  </p>
                </div>
              </div>

              {isLoadingBudgets ? (
                <Badge
                  variant="outline"
                  className="bg-yellow-50 text-yellow-700 border-yellow-200"
                >
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Cargando
                </Badge>
              ) : budgetData.length === 0 ? (
                <Badge
                  variant="outline"
                  className="bg-slate-50 text-slate-700 border-slate-200"
                >
                  Sin Datos
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-200"
                >
                  {budgetData.length} Presupuestos
                </Badge>
              )}
            </div>
          </div>

          {budgetData.length === 0 && !isLoadingBudgets && (
            <div className="px-6 py-4">
              <Alert className="border-amber-200 bg-amber-50">
                <Info className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <div className="space-y-2">
                    <p className="font-medium">
                      No hay presupuestos disponibles.
                    </p>
                    <p className="text-sm">
                      Para comenzar, crea un nuevo presupuesto o carga desde un
                      archivo Excel.
                    </p>
                    <div className="flex space-x-2 mt-3">
                      <Button
                        onClick={() => setShowCreateModal(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Crear Presupuesto
                      </Button>
                      <Button
                        onClick={() => setShowUploadModal(true)}
                        variant="outline"
                        className="border-green-300 text-green-600 hover:bg-green-50"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Cargar desde Excel
                      </Button>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 sticky left-0 bg-slate-50 z-10">
                    Nombre
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-900">
                    Monto
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-900">
                    Gastado
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-900">
                    Disponible
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-900">
                    Estado
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-900">
                    Período
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-900">
                    Centro de Costo
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredData.length === 0 && !isLoadingBudgets ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center space-y-4">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                          <BarChart3 className="h-8 w-8 text-slate-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-slate-900">
                            No hay presupuestos
                          </h3>
                          <p className="text-slate-500 mt-1">
                            No se encontraron presupuestos con los filtros
                            aplicados
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredData.map((budget, index) => {
                    const available =
                      parseFloat(budget.amount || 0) -
                      parseFloat(budget.spent_amount || 0);
                    const spentPercentage =
                      budget.amount > 0
                        ? (parseFloat(budget.spent_amount || 0) /
                            parseFloat(budget.amount)) *
                          100
                        : 0;

                    const isEditing = editingRow === index;

                    return (
                      <motion.tr
                        key={budget.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`hover:bg-slate-50 transition-colors ${
                          isEditing ? "bg-blue-50" : ""
                        }`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900 sticky left-0 bg-white hover:bg-slate-50 z-10">
                          {isEditing ? (
                            <Input
                              value={editedData.name || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  name: e.target.value,
                                })
                              }
                              placeholder="Nombre del presupuesto"
                              className="w-full"
                            />
                          ) : (
                            <div className="flex items-center space-x-3">
                              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-emerald-500"></div>
                              <div>
                                <div className="font-medium">{budget.name}</div>
                                {budget.description && (
                                  <div className="text-xs text-slate-500 truncate max-w-xs">
                                    {budget.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700 text-center">
                          {isEditing ? (
                            <div className="space-y-2">
                              <Input
                                type="number"
                                value={editedData.amount || ""}
                                onChange={(e) =>
                                  setEditedData({
                                    ...editedData,
                                    amount: e.target.value,
                                  })
                                }
                                placeholder="Monto"
                                className="w-full"
                                min="0"
                                step="1000"
                              />
                              <Select
                                value={editedData.currency || "COP"}
                                onValueChange={(value) =>
                                  setEditedData({
                                    ...editedData,
                                    currency: value,
                                  })
                                }
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="COP">COP</SelectItem>
                                  <SelectItem value="USD">USD</SelectItem>
                                  <SelectItem value="EUR">EUR</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <span className="font-mono font-semibold">
                              {formatCurrency(budget.amount, budget.currency)}
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700 text-center">
                          <div className="space-y-1">
                            <span className="font-mono">
                              {formatCurrency(
                                budget.spent_amount,
                                budget.currency
                              )}
                            </span>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  spentPercentage > 90
                                    ? "bg-red-500"
                                    : spentPercentage > 75
                                    ? "bg-orange-500"
                                    : "bg-green-500"
                                }`}
                                style={{
                                  width: `${Math.min(spentPercentage, 100)}%`,
                                }}
                              ></div>
                            </div>
                            <span className="text-xs text-slate-500">
                              {spentPercentage.toFixed(1)}%
                            </span>
                          </div>
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700 text-center">
                          <span
                            className={`font-mono font-semibold ${
                              available < 0
                                ? "text-red-600"
                                : available <
                                  parseFloat(budget.amount || 0) * 0.1
                                ? "text-orange-600"
                                : "text-green-600"
                            }`}
                          >
                            {formatCurrency(available, budget.currency)}
                          </span>
                        </td>

                        <td className="px-4 py-4 text-center">
                          {isEditing ? (
                            <Select
                              value={editedData.status || "active"}
                              onValueChange={(value) =>
                                setEditedData({
                                  ...editedData,
                                  status: value,
                                })
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">Activo</SelectItem>
                                <SelectItem value="inactive">
                                  Inactivo
                                </SelectItem>
                                <SelectItem value="completed">
                                  Completado
                                </SelectItem>
                                <SelectItem value="cancelled">
                                  Cancelado
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge
                              variant="outline"
                              className={getStatusBadgeColor(budget.status)}
                            >
                              {budget.status}
                            </Badge>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700 text-center">
                          {isEditing ? (
                            <div className="space-y-2">
                              <Input
                                type="date"
                                value={editedData.start_date || ""}
                                onChange={(e) =>
                                  setEditedData({
                                    ...editedData,
                                    start_date: e.target.value,
                                  })
                                }
                                className="w-full text-xs"
                              />
                              <Input
                                type="date"
                                value={editedData.end_date || ""}
                                onChange={(e) =>
                                  setEditedData({
                                    ...editedData,
                                    end_date: e.target.value,
                                  })
                                }
                                className="w-full text-xs"
                              />
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className="flex items-center justify-center space-x-1">
                                <Calendar className="h-3 w-3 text-slate-400" />
                                <span className="text-xs">
                                  {formatDate(budget.start_date)}
                                </span>
                              </div>
                              <div className="flex items-center justify-center space-x-1">
                                <Calendar className="h-3 w-3 text-slate-400" />
                                <span className="text-xs">
                                  {formatDate(budget.end_date)}
                                </span>
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700 text-center">
                          {isEditing ? (
                            <Select
                              value={editedData.cost_center_id || "none"}
                              onValueChange={(value) =>
                                setEditedData({
                                  ...editedData,
                                  cost_center_id:
                                    value === "none" ? null : value,
                                })
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Centro de costo" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  Sin centro de costo
                                </SelectItem>
                                {costCenters.map((center) => (
                                  <SelectItem key={center.id} value={center.id}>
                                    {center.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="flex items-center justify-center space-x-1">
                              <Building2 className="h-3 w-3 text-slate-400" />
                              <span className="text-xs">
                                {budget.cost_center_name || "N/A"}
                              </span>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleSaveEdit}
                                  disabled={loading}
                                  className="border-green-300 text-green-600 hover:bg-green-50"
                                >
                                  <Save className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelEdit}
                                  disabled={loading}
                                  className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStartEdit(index, budget)}
                                  disabled={editingRow !== null || loading}
                                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                >
                                  <Edit3 className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleDeleteClick(budget.id, budget.name)
                                  }
                                  disabled={
                                    editingRow !== null ||
                                    isDeleting === budget.id ||
                                    loading
                                  }
                                  className="border-red-300 text-red-600 hover:bg-red-50"
                                >
                                  {isDeleting === budget.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3 w-3" />
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadSuccess}
        isUploading={loading}
        title="Carga Masiva de Presupuestos"
        config={uploadConfig}
        uploadResult={uploadResult}
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span>Confirmar Eliminación</span>
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Esta acción no se puede deshacer. El presupuesto será eliminado
              permanentemente.
            </DialogDescription>
          </DialogHeader>

          {budgetToDelete && (
            <div className="bg-red-50 border border-red-200 rounded p-4 space-y-4">
              <div className="flex items-center space-x-2 mb-3">
                <Trash2 className="h-4 w-4 text-red-600" />
                <span className="font-medium text-red-900">
                  Presupuesto a eliminar:
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-red-600 uppercase tracking-wide">
                    Nombre
                  </label>
                  <p className="text-sm font-semibold text-red-900">
                    {budgetToDelete.name}
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="space-x-2">
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar Presupuesto
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Budget Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-blue-700">
              <Plus className="h-5 w-5" />
              <span>Crear Nuevo Presupuesto</span>
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Complete la información para crear un nuevo presupuesto.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Información básica */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">
                Información Básica
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budget-name">Nombre del Presupuesto *</Label>
                  <Input
                    id="budget-name"
                    value={newBudget.name}
                    onChange={(e) =>
                      setNewBudget({ ...newBudget, name: e.target.value })
                    }
                    placeholder="Ej: Presupuesto Marketing 2025"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="budget-amount">Monto *</Label>
                  <Input
                    id="budget-amount"
                    type="number"
                    value={newBudget.amount}
                    onChange={(e) =>
                      setNewBudget({ ...newBudget, amount: e.target.value })
                    }
                    placeholder="100000000"
                    className="mt-1"
                    min="0"
                    step="1000"
                  />
                </div>

                <div>
                  <Label htmlFor="budget-currency">Moneda *</Label>
                  <Select
                    value={newBudget.currency}
                    onValueChange={(value) =>
                      setNewBudget({ ...newBudget, currency: value })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="COP">COP - Peso Colombiano</SelectItem>
                      <SelectItem value="USD">USD - Dólar Americano</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="budget-cost-center">Centro de Costo</Label>
                  <Select
                    value={newBudget.cost_center_id}
                    onValueChange={(value) =>
                      setNewBudget({ ...newBudget, cost_center_id: value })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccionar centro de costo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin centro de costo</SelectItem>
                      {costCenters.map((center) => (
                        <SelectItem key={center.id} value={center.id}>
                          {center.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="budget-description">Descripción</Label>
                <Input
                  id="budget-description"
                  value={newBudget.description}
                  onChange={(e) =>
                    setNewBudget({ ...newBudget, description: e.target.value })
                  }
                  placeholder="Descripción detallada del presupuesto"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Fechas */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">
                Período
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budget-start-date">Fecha de Inicio</Label>
                  <Input
                    id="budget-start-date"
                    type="date"
                    value={newBudget.start_date}
                    onChange={(e) =>
                      setNewBudget({ ...newBudget, start_date: e.target.value })
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="budget-end-date">Fecha de Fin</Label>
                  <Input
                    id="budget-end-date"
                    type="date"
                    value={newBudget.end_date}
                    onChange={(e) =>
                      setNewBudget({ ...newBudget, end_date: e.target.value })
                    }
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="space-x-2">
            <Button
              variant="outline"
              onClick={handleCancelCreate}
              disabled={isCreating}
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleCreateBudget}
              disabled={isCreating}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Presupuesto
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}